/**
 * Typed client for the entitlements admin routes (method access, effective
 * access, cache clear, FileCenter all-folders, identity activity).
 *
 * Uses `fetch` rather than axios: CRA's Jest cannot transform axios v1 ESM.
 * Every response is wrapped `{ meta, request, response: { code, data } }`; each
 * function returns `response.data`. Errors carry FastAPI's `{ detail }`.
 */
import { getHeaders } from "../../ApiHeaders/apiHeadersEntitlements";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface MethodAccessEntry {
  http_method: HttpMethod;
  access_level_name: string;
}

export interface MethodAccess {
  permission_object_id: number;
  po_path: string;
  entries: MethodAccessEntry[];
}

export interface GrantedObject {
  permission_object_id: number;
  permission_object_name: string;
  po_path: string;
  po_type_code: string;
  service_name: string | null;
  access_level_name: string;
  via_group: string;
  via_role: string;
  method_access: Record<string, string>;
}

export interface GrantedCollection {
  collection_id: number;
  collection_name: string;
  domain: string;
  asset_count: number;
  via_role: string;
}

export interface RoleMember {
  user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  enabled: string | null;
}

export interface RoleRef {
  role_id: number;
  role_name: string;
  domain: string;
}

export interface RoleEffectiveAccess {
  role_id: number;
  role_name: string;
  domain: string;
  ui_pages: GrantedObject[];
  api_permissions: GrantedObject[];
  asset_groups: GrantedCollection[];
  members: RoleMember[];
}

export interface UserEffectiveAccess {
  user_id: number;
  email: string | null;
  tenant_type: string;
  /** "Y"/"N"; a disabled user has roles but no pages, APIs or groups. */
  enabled: string;
  roles: RoleRef[];
  ui_pages: GrantedObject[];
  api_permissions: GrantedObject[];
  asset_groups: GrantedCollection[];
}

export interface AllFoldersLevel {
  permission_object_id: number | null;
  mapped: boolean;
}

export interface AllFoldersStatus {
  asset_id: number;
  complete: boolean;
  write: AllFoldersLevel;
  read: AllFoldersLevel;
}

export interface ActivitySummary {
  total_identities: number;
  active_last_30_days: number;
  dormant: number;
  anonymous: number;
}

export interface ActivityHistoryPoint {
  /** YYYY-MM-DD for day, YYYY-MM for month */
  bucket: string;
  call_count: number;
}

export class EntitlementsApiError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = "EntitlementsApiError";
    this.status = status;
    this.detail = detail;
    Object.setPrototypeOf(this, EntitlementsApiError.prototype);
  }
}

const baseUrl = () => process.env.REACT_APP_ENTITLEMENTS_API || "";

async function request<T>(
  path: string,
  method: "GET" | "POST" | "PUT" = "GET",
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { ...(await getHeaders()) };
  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`${baseUrl()}${path}`, init);
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const raw = json?.detail;
    const detail =
      typeof raw === "string"
        ? raw
        : raw !== undefined
          ? JSON.stringify(raw)
          : res.statusText || `HTTP ${res.status}`;
    throw new EntitlementsApiError(res.status, detail);
  }
  return json?.response?.data as T;
}

export const getMethodAccess = (poId: number) =>
  request<MethodAccess>(`/permission-object/${poId}/method-access`);

export const putMethodAccess = (poId: number, entries: MethodAccessEntry[]) =>
  request<MethodAccess>(`/permission-object/${poId}/method-access`, "PUT", {
    entries,
  });

export const getRoleEffectiveAccess = (roleId: number) =>
  request<RoleEffectiveAccess>(`/role/${roleId}/effective-access`);

export const getUserEffectiveAccess = (userId: number) =>
  request<UserEffectiveAccess>(`/user/${userId}/effective-access`);

/** Clears one user's entitlement cache in both the amp and mobile domains. */
export async function clearUserCache(
  entraObjectId: string,
): Promise<{ amp: number; mobile: number }> {
  // An empty user_id would clear every user's entries.
  if (!entraObjectId || !entraObjectId.trim()) {
    throw new Error("A user id is required to clear a user's cache.");
  }
  const clear = async (domain: string, service: string) => {
    const qs = new URLSearchParams({
      domain,
      service_name: service,
      user_id: entraObjectId,
    });
    const data = await request<{ keys_deleted?: unknown } | null>(
      `/cache/clear?${qs.toString()}`,
      "POST",
    );
    // A body without a numeric keys_deleted counts as nothing cleared, not NaN.
    const n = Number(data?.keys_deleted);
    return Number.isFinite(n) ? n : 0;
  };
  const [amp, mobile] = await Promise.all([
    clear("amp", "general_amp_service"),
    clear("mobile", "general_mobile_service"),
  ]);
  return { amp, mobile };
}

/** Object (asset) ids mapped to one collection (asset group). */
export const getCollectionObjectIds = async (collectionId: number): Promise<number[]> => {
  const data = await request<{ object_ids?: number[] } | null>(
    `/collection-mapping/collection/${collectionId}/objects`,
  );
  return data?.object_ids ?? [];
};

export const getAllFoldersStatus = (assetId: number) =>
  request<AllFoldersStatus>(`/folder-permission/asset/${assetId}/all-folders`);

export const grantAllFolders = (assetId: number) =>
  request<AllFoldersStatus>(
    `/folder-permission/asset/${assetId}/all-folders`,
    "POST",
  );

export const getActivitySummary = (inactiveSinceDays: number) =>
  request<ActivitySummary>(
    `/entitlements/activity/summary?inactive_since_days=${inactiveSinceDays}`,
  );

export const getIdentityHistory = (
  identityId: string,
  granularity: "day" | "month",
) =>
  request<ActivityHistoryPoint[]>(
    `/entitlements/activity/history?identity_id=${encodeURIComponent(identityId)}&granularity=${granularity}`,
  );
